import { Tour, TourStep, useTour, useTours } from '@tour-kit/react';
import { useCallback, useEffect, useRef } from 'react';

import {
  GUIDANCE_TARGET,
  ORIENTATION_TOUR_ID,
  guidanceTargetSelector,
  type GuidanceTarget,
} from '@/core/guidance/constants';
import { useGuidanceStore } from '@/core/guidance/guidance.store';
import { OrientationTourChrome } from '@/core/guidance/orientation-tour-card';
import {
  browserOrientationStorage,
  hasSeenOrientationTour,
  markOrientationTourSeen,
} from '@/core/guidance/orientation-tour-seen';
import { WelcomeStepContent } from '@/core/guidance/welcome-step-content';
import { useShellSidebarOverlay } from '@/core/hooks/use-mobile';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useAuthStore } from '@/core/store/auth.store';
import { shellUsesMobileTabBar } from '@/core/utils/shell-chrome';

function OrientationTourController() {
  const { start, isActive } = useTour(ORIENTATION_TOUR_ID);
  const { isTourCompleted, isTourSkipped } = useTours();
  const setTourActive = useGuidanceStore((s) => s.setTourActive);
  const replayNonce = useGuidanceStore((s) => s.replayNonce);
  const lastReplayRef = useRef(0);
  const autoStartedRef = useRef(false);
  const startRef = useRef(start);
  const isTourCompletedRef = useRef(isTourCompleted);
  const isTourSkippedRef = useRef(isTourSkipped);
  useEffect(() => {
    startRef.current = start;
    isTourCompletedRef.current = isTourCompleted;
    isTourSkippedRef.current = isTourSkipped;
  });

  useEffect(() => {
    setTourActive(isActive);
    return () => setTourActive(false);
  }, [isActive, setTourActive]);

  useEffect(() => {
    if (replayNonce === 0 || replayNonce === lastReplayRef.current) return;
    lastReplayRef.current = replayNonce;
    start();
  }, [replayNonce, start]);

  useEffect(() => {
    // Once per user. Workspace switches remount checklist chrome; that must
    // not replay the welcome tour. Help → Intro erneut ansehen uses replayNonce.
    if (autoStartedRef.current) return;
    const userId = useAuthStore.getState().user?.id;
    const storage = browserOrientationStorage();
    if (
      hasSeenOrientationTour(userId, storage) ||
      isTourCompletedRef.current(ORIENTATION_TOUR_ID) ||
      isTourSkippedRef.current(ORIENTATION_TOUR_ID)
    ) {
      autoStartedRef.current = true;
      return;
    }

    const timer = window.setTimeout(() => {
      if (autoStartedRef.current) return;
      const nextUserId = useAuthStore.getState().user?.id;
      const nextStorage = browserOrientationStorage();
      if (
        hasSeenOrientationTour(nextUserId, nextStorage) ||
        isTourCompletedRef.current(ORIENTATION_TOUR_ID) ||
        isTourSkippedRef.current(ORIENTATION_TOUR_ID)
      ) {
        autoStartedRef.current = true;
        return;
      }
      autoStartedRef.current = true;
      markOrientationTourSeen(nextUserId, nextStorage);
      startRef.current();
    }, 400);

    return () => window.clearTimeout(timer);
  }, []);

  return null;
}

export function OrientationTour() {
  const dict = useDictionary();
  const steps = dict.guidance.tours.orientation.steps;
  const usesMobileTabs = shellUsesMobileTabBar();
  const sidebarIsDrawer = useShellSidebarOverlay();
  const { navigateShell } = useNavigateShell();
  /**
   * Without the real „Loslegen“ launcher (dismissed, complete, not mounted)
   * the last step had no target: the tour stopped after step 6 and the meter
   * still showed 7 (DEV-574). Then step 6 is the last one („Fertig“).
   */
  const checklistLauncherVisible = useGuidanceStore(
    (s) => s.isChecklistLauncherVisible,
  );
  /** Where the tour was started; skip/close returns there (DEV-574). */
  const startPathRef = useRef<string | null>(null);

  /**
   * Browser below `xl`: the sidebar is a closed drawer, so its nav anchors are
   * not in the DOM and TourKit stops the tour (TARGET_NOT_FOUND). Show those
   * steps as a centred card on the welcome host instead.
   */
  const navAnchorsHidden = !usesMobileTabs && sidebarIsDrawer;
  const navTarget = (target: GuidanceTarget) =>
    guidanceTargetSelector(
      navAnchorsHidden ? GUIDANCE_TARGET.welcomeHost : target,
    );
  const navPlacement = (placement: 'top' | 'right') =>
    navAnchorsHidden ? 'bottom' : placement;

  /** Status board is the post-tour landing after „Fertig“. */
  const goHome = useCallback(() => {
    navigateShell(shellPaths.home);
  }, [navigateShell]);
  const rememberStartPath = useCallback(() => {
    startPathRef.current = window.location.pathname;
  }, []);
  /** Skip / close returns to the page the tour was started from. */
  const goBackToStart = useCallback(() => {
    const startPath = startPathRef.current;
    startPathRef.current = null;
    if (startPath && startPath !== window.location.pathname) {
      navigateShell(startPath);
    }
  }, [navigateShell]);

  return (
    <>
      <Tour
        id={ORIENTATION_TOUR_ID}
        onStart={rememberStartPath}
        onComplete={goHome}
        onSkip={goBackToStart}
      >
        <TourStep
          id="welcome"
          target={guidanceTargetSelector(GUIDANCE_TARGET.welcomeHost)}
          title={steps.welcome.title}
          content={<WelcomeStepContent />}
          placement="bottom"
          showProgress={false}
          waitForTarget
        />
        <TourStep
          id="home"
          target={navTarget(GUIDANCE_TARGET.home)}
          title={steps.home.title}
          content={steps.home.body}
          placement={navPlacement('right')}
          route={shellPaths.home}
          routeMatch="startsWith"
          waitForTarget
        />
        <TourStep
          id="profiles"
          target={navTarget(GUIDANCE_TARGET.profiles)}
          title={steps.profiles.title}
          content={steps.profiles.body}
          placement={navPlacement(usesMobileTabs ? 'top' : 'right')}
          route={shellPaths.profiles}
          routeMatch="startsWith"
          waitForTarget
        />
        <TourStep
          id="leads"
          target={navTarget(GUIDANCE_TARGET.leads)}
          title={steps.leads.title}
          content={steps.leads.body}
          placement={navPlacement(usesMobileTabs ? 'top' : 'right')}
          route={shellPaths.leads}
          routeMatch="startsWith"
          waitForTarget
        />
        <TourStep
          id="leads-views"
          target={guidanceTargetSelector(GUIDANCE_TARGET.leadsToolbar)}
          title={steps.leadsViews.title}
          content={steps.leadsViews.body}
          placement="bottom"
          route={shellPaths.leads}
          routeMatch="exact"
          waitForTarget
          // The scope row mounts once the Leads page has loaded its profile.
          waitTimeout={8000}
        />
        <TourStep
          id="more"
          target={navTarget(
            usesMobileTabs ? GUIDANCE_TARGET.more : GUIDANCE_TARGET.navProducts,
          )}
          title={
            usesMobileTabs
              ? (steps.more.titleMobile ?? steps.more.title)
              : steps.more.title
          }
          content={
            usesMobileTabs
              ? (steps.more.bodyMobile ?? steps.more.body)
              : navAnchorsHidden
                ? steps.more.bodyMenu
                : steps.more.body
          }
          placement={navPlacement(usesMobileTabs ? 'top' : 'right')}
          route={usesMobileTabs ? shellPaths.moreHub : shellPaths.moreProducts}
          routeMatch="startsWith"
          waitForTarget
        />
        {checklistLauncherVisible ? (
          <TourStep
            id="checklist"
            target={guidanceTargetSelector(GUIDANCE_TARGET.checklistLauncher)}
            title={steps.checklist.title}
            content={steps.checklist.body}
            placement="left"
            route={shellPaths.home}
            routeMatch="startsWith"
            waitForTarget
          />
        ) : null}
      </Tour>

      <OrientationTourChrome />
      <OrientationTourController />
    </>
  );
}
